import { useState, type ChangeEvent, type FormEvent } from 'react'
import { useAuthStore } from '../../store/authStore'
import { submitReport, uploadReportPhotos } from '../../api/reports'
import { ALL_EVENT_TYPES, ALL_SPECIES_BUCKETS, EVENT_TYPE_LABELS, SPECIES_LABELS } from '../../constants/taxonomy'
import type { EventType, SpeciesBucket } from '../../types/contact'

const MAX_PHOTOS = 3
const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024

interface ReportFormProps {
  onClose: () => void
  onSubmitted: () => void
}

// The browser's generic "location unavailable" case has three genuinely different causes
// that call for different next steps — worth distinguishing rather than one catch-all message
// ("could not get your location"), since a user can't tell "you denied permission" apart from
// "GPS just timed out" without this.
function geolocationErrorMessage(err: GeolocationPositionError): string {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return 'Location access is blocked for this site. On iPhone: tap the "AA" icon in the address bar → Website Settings → Location → Allow. Or enter coordinates manually.'
    case err.POSITION_UNAVAILABLE:
      return "Your device couldn't determine a location right now (no GPS/network signal). Try again outdoors, or enter coordinates manually."
    case err.TIMEOUT:
      return 'Getting your location took too long. Try again, or enter coordinates manually.'
    default:
      return 'Could not get your location — enter coordinates manually.'
  }
}

function nowLocalDatetime(): string {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}

export default function ReportForm({ onClose, onSubmitted }: ReportFormProps) {
  const token = useAuthStore((s) => s.token)
  const [step, setStep] = useState<'form' | 'confirm'>('form')
  const [speciesBucket, setSpeciesBucket] = useState<SpeciesBucket>('backyard_poultry')
  const [eventType, setEventType] = useState<EventType>('sick_birds')
  const [description, setDescription] = useState('')
  const [observedAt, setObservedAt] = useState(nowLocalDatetime())
  const [lat, setLat] = useState<number | null>(null)
  const [lon, setLon] = useState<number | null>(null)
  const [accuracyM, setAccuracyM] = useState<number | null>(null)
  const [locating, setLocating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [photos, setPhotos] = useState<File[]>([])
  const [photoWarning, setPhotoWarning] = useState<string | null>(null)

  const handlePhotoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = '' // allow re-selecting the same file after removal

    const combined = [...photos, ...files]
    if (combined.length > MAX_PHOTOS) {
      setError(`You can attach at most ${MAX_PHOTOS} photos.`)
      return
    }
    const tooBig = files.find((f) => f.size > MAX_PHOTO_SIZE_BYTES)
    if (tooBig) {
      setError(`${tooBig.name} is over the 5MB limit.`)
      return
    }
    setError(null)
    setPhotos(combined)
  }

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index))
  }

  const handleUseGps = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not available in this browser — enter coordinates manually.')
      return
    }
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude)
        setLon(pos.coords.longitude)
        setAccuracyM(pos.coords.accuracy)
        setLocating(false)
      },
      (err) => {
        setError(geolocationErrorMessage(err))
        setLocating(false)
      },
    )
  }

  function handleReview(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (lat === null || lon === null) {
      setError('Set a location first (use GPS or enter coordinates).')
      return
    }
    if (!token) {
      setError('You must be signed in to submit a report.')
      return
    }
    setStep('confirm')
  }

  async function handleConfirmSubmit() {
    setError(null)
    if (lat === null || lon === null || !token) {
      // Shouldn't be reachable — handleReview already validated both — but keeps this
      // function safe to call standalone.
      setStep('form')
      return
    }
    setSubmitting(true)
    try {
      const report = await submitReport(token, {
        species_bucket: speciesBucket,
        event_type: eventType,
        description: description || undefined,
        lat,
        lon,
        accuracy_m: accuracyM ?? undefined,
        observed_at: new Date(observedAt).toISOString(),
      })

      if (photos.length > 0) {
        try {
          await uploadReportPhotos(token, report.id, photos)
        } catch (photoErr) {
          // The report itself already succeeded — don't lose that. Keep the form open just to
          // show this warning (the parent closes it on onSubmitted, which would hide the message).
          setPhotoWarning(
            photoErr instanceof Error ? photoErr.message : 'Report saved, but photo upload failed.',
          )
          return
        }
      }

      onSubmitted()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit report')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {step === 'form' ? 'Report a potential contact' : 'Review before submitting'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        {step === 'form' && (
          <p className="text-xs text-gray-500 mb-4">
            This is a low-confidence situational report, not a formal biosecurity notification. If
            you suspect an emergency animal disease, also call the Emergency Animal Disease Watch
            Hotline on 1800 675 888.
          </p>
        )}

        {photoWarning && (
          <div className="rounded-lg bg-amber-50 border border-amber-300 text-amber-800 px-3 py-2 text-sm mb-4">
            <p className="mb-2">{photoWarning}</p>
            <button
              type="button"
              onClick={onSubmitted}
              className="text-amber-900 font-semibold underline"
            >
              OK
            </button>
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-300 text-red-700 px-3 py-2 text-sm mb-4">
            {error}
          </div>
        )}

        {!photoWarning && step === 'form' && (
          <form onSubmit={handleReview} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Species</label>
              <select
                value={speciesBucket}
                onChange={(e) => setSpeciesBucket(e.target.value as SpeciesBucket)}
                className="w-full rounded-lg bg-gray-100 border border-gray-300 text-gray-900 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {ALL_SPECIES_BUCKETS.map((b) => (
                  <option key={b} value={b}>{SPECIES_LABELS[b]}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">What did you see?</label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value as EventType)}
                className="w-full rounded-lg bg-gray-100 border border-gray-300 text-gray-900 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {ALL_EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>{EVENT_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">When did you observe this?</label>
              <input
                type="datetime-local"
                value={observedAt}
                max={nowLocalDatetime()}
                onChange={(e) => setObservedAt(e.target.value)}
                className="w-full rounded-lg bg-gray-100 border border-gray-300 text-gray-900 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <button
                type="button"
                onClick={handleUseGps}
                disabled={locating}
                className="w-full rounded-lg border border-blue-300 bg-blue-50 text-blue-700 text-sm font-medium py-2 mb-2 disabled:opacity-60"
              >
                {locating ? 'Getting location…' : lat !== null ? 'Update GPS location' : 'Use my current location'}
              </button>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  step="any"
                  placeholder="Latitude"
                  value={lat ?? ''}
                  onChange={(e) => setLat(e.target.value ? Number(e.target.value) : null)}
                  className="rounded-lg bg-gray-100 border border-gray-300 text-gray-900 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="number"
                  step="any"
                  placeholder="Longitude"
                  value={lon ?? ''}
                  onChange={(e) => setLon(e.target.value ? Number(e.target.value) : null)}
                  className="rounded-lg bg-gray-100 border border-gray-300 text-gray-900 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={2000}
                className="w-full rounded-lg bg-gray-100 border border-gray-300 text-gray-900 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Photos <span className="text-gray-400 font-normal">(optional, up to {MAX_PHOTOS}, 5MB each)</span>
              </label>
              {photos.length > 0 && (
                <ul className="mb-2 space-y-1">
                  {photos.map((f, i) => (
                    <li
                      key={`${f.name}-${i}`}
                      className="flex items-center justify-between text-sm bg-gray-100 rounded-lg px-3 py-1.5"
                    >
                      <span className="truncate">{f.name}</span>
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        className="text-gray-400 hover:text-gray-600 ml-2"
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {photos.length < MAX_PHOTOS && (
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  onChange={handlePhotoChange}
                  className="w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 file:px-3 file:py-2 file:text-sm file:font-medium"
                />
              )}
            </div>

            <button
              type="submit"
              className="w-full rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 transition"
            >
              Review &amp; submit
            </button>
          </form>
        )}

        {!photoWarning && step === 'confirm' && (
          <div className="space-y-4">
            <div className="rounded-xl border-2 border-red-300 bg-red-50 px-4 py-3">
              <p className="text-sm font-semibold text-red-800 mb-1">
                This is not an official biosecurity notification.
              </p>
              <p className="text-sm text-red-700">
                This report supports situational awareness for DAFF and state biosecurity
                agencies — it does not replace them. If you suspect an emergency animal disease,
                call the Emergency Animal Disease Watch Hotline on{' '}
                <span className="font-semibold">1800 675 888</span> (free, 24/7), every time,
                regardless of what you submit here.
              </p>
            </div>

            <div className="rounded-xl border-2 border-blue-200 bg-blue-50 px-4 py-3">
              <p className="text-sm font-semibold text-blue-900 mb-1">
                Your account email will be attached to this entry.
              </p>
              <p className="text-sm text-blue-800">
                It's permanently recorded against this report for accountability — see our{' '}
                <a href="/privacy" target="_blank" className="underline font-medium">Privacy Policy</a>
                {' '}— but is never shown publicly or to other users.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStep('form')}
                disabled={submitting}
                className="flex-1 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold py-2.5 disabled:opacity-60"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                disabled={submitting}
                className="flex-1 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-blue-200 disabled:text-blue-500 text-white text-sm font-semibold py-2.5 transition"
              >
                {submitting ? 'Submitting…' : 'Confirm and submit'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
